import { useCallback, useEffect, useState } from "react";

// ─── Styles ────────────────────────────────────────────────
import styles from "./Organisation.module.scss";

// ─── Components ────────────────────────────────────────────
import { ReactComponent as LeftArrow } from "../../assets/svg/leftArrow.svg";

// ─── Store ─────────────────────────────────────────────────
import { useOrganisationStore } from "../../providers/organisation/organisation.provider";

// ─── Libraries ─────────────────────────────────────────────
import { useTranslation } from "react-i18next";
import BreadCrumbs from "../../components/breadcrumbs";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../../setup/constants";
import BusinessSettings from "./settings";
import Avatar, { AvatarSize } from "../../components/common/Avatar";
import classNames from "classnames";
import useGetContractStats from "../../hooks/useGetContractStats";
import { formatAmount } from "../../setup/util/wallet";
import { getOrganizationWalletAmounts } from "../../setup/api/services/wallet";
import { WalletAmountsResponseData } from "../../setup/api/services/wallet/models";
import { ECONTRACT_STATUS } from "../../enums";
import { getOrgOwnerByOrgCode } from "../../setup/api/services/user";
import { IClientAdminResponseBody } from "../../setup/api/services/user/models";

interface OrganizationDetailsProps {
  /** Opened from the Service Providers tab: adds the shipping-fee settlement tab. */
  isServiceProvider?: boolean;
}

const OrganizationDetails = ({
  isServiceProvider = false,
}: OrganizationDetailsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { code } = useParams();

  const [stats, setStats] = useState<WalletAmountsResponseData["data"]>();
  const [orgOwner, setOrgOwner] = useState<IClientAdminResponseBody>();

  const {
    totalAvailableCount: availableContractsCount,
    data: contractStats,
    totalCount,
  } = useGetContractStats(undefined, code);

  // ─── Zustand Store Selectors ─────────────────────────────
  const fetchOrganization = useOrganisationStore(
    (state) => state.fetchOrganization
  );
  const organization = useOrganisationStore(
    (state) => state.selectedOrganisation
  );

  const fetchOrgData = useCallback(async () => {
    const adminRes = await getOrgOwnerByOrgCode(code || "");
    const res = await getOrganizationWalletAmounts(
      `${adminRes?.data?.userIdentification}`
    );

    setOrgOwner(adminRes?.data);
    setStats(res?.data);
  }, [code]);

  useEffect(() => {
    if (code) {
      fetchOrganization(code);
      fetchOrgData();
    }
  }, [fetchOrganization, code, fetchOrgData]);

  // ─── Render ──────────────────────────────────────────────
  return (
    <div className={styles["page_layout"]}>
      <div className={styles.breadcrumbs_wrapper}>
        <BreadCrumbs
          activeIndex={2}
          separator={<LeftArrow />}
          items={[
            {
              label: t("lbl_business_home"),
              onClick: () => navigate(ROUTES.HOME),
              visited: true,
            },
            {
              label: isServiceProvider ? "مزودي الخدمة" : "الشركات",
              visited: true,
              onClick: () =>
                navigate(
                  isServiceProvider
                    ? ROUTES.SERVICE_PROVIDERS
                    : ROUTES.ORGANIZATIONS
                ),
            },
            {
              label: organization?.name || "",
            },
          ]}
        />
      </div>
      <div className={styles.details_header_card}>
        <Avatar
          avatarSize={AvatarSize.SMALL}
          src={organization?.logoUrl}
          alt={organization?.name}
        />
        <div className={styles.info_column}>
          <div className={styles.header_top_row}>
            <div className={styles.title_group}>
              <h3 className={styles.org_name}>{organization?.name}</h3>
              <p className={styles.org_desc}>{organization?.description}</p>
            </div>
            <div
              className={classNames(styles.statusBadge, styles.statusApproved)}>
              مفعل
            </div>
          </div>
          <div className={styles.section_wrapper}>
            <h3 className={styles.section_title}>معلومات</h3>
            <div className={styles.info_stats_card}>
              <div className={styles.info_grid}>
                <div className={styles.info_item}>
                  <p className={styles.info_label}>الموقع</p>
                  <h4
                    className={classNames(
                      styles.info_value,
                      styles.link,
                      styles.ltr
                    )}>
                    {organization?.websiteUrl}
                  </h4>
                </div>
                <div className={classNames(styles.info_item, styles.bordered)}>
                  <p className={styles.info_label}>تاريخ الانضمام</p>
                  <h4 className={styles.info_value}>
                    {orgOwner?.registrationDate
                      ? new Date(orgOwner?.registrationDate).toLocaleDateString(
                          "ar-EG",
                          {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          }
                        )
                      : ""}
                  </h4>
                </div>
                <div className={classNames(styles.info_item, styles.bordered)}>
                  <p className={styles.info_label}>الكود</p>
                  <h4 className={classNames(styles.info_value, styles.ltr)}>
                    #{organization?.code}
                  </h4>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.section_wrapper}>
            <h3 className={styles.section_title}>إحصائيات</h3>
            <div className={styles.info_stats_card}>
              <div className={styles.info_grid}>
                <div className={styles.info_item}>
                  <p className={styles.info_label}>إجمالي عدد المعاملات</p>
                  <h4 className={styles.info_value}>
                    {formatAmount(totalCount)}
                  </h4>
                </div>
                <div className={classNames(styles.info_item, styles.bordered)}>
                  <p className={styles.info_label}>المعاملات الجارية</p>
                  <h4 className={styles.info_value}>
                    {formatAmount(availableContractsCount)}
                  </h4>
                </div>
                <div className={classNames(styles.info_item, styles.bordered)}>
                  <p className={styles.info_label}>إجمالي قيمة المعاملات</p>
                  <h4 className={classNames(styles.info_value, styles.dimmed)}>
                    {`${formatAmount(
                      Object.values(
                        contractStats as {
                          [key: string]: { totalAmount: number };
                        }
                      ).reduce((sum, val) => sum + val?.totalAmount, 0)
                    )} ${t("lbl_saudi_currency")}`}
                  </h4>
                </div>
                <div className={classNames(styles.info_item, styles.bordered)}>
                  <p className={styles.info_label}>قيمة المعاملات الجارية</p>
                  <h4 className={classNames(styles.info_value, styles.dimmed)}>
                    {`${formatAmount(
                      Object.entries(
                        contractStats as {
                          [key: string]: { totalAmount: number };
                        }
                      ).reduce(
                        (sum, val) =>
                          sum +
                          (val?.[0] === ECONTRACT_STATUS.CREATED ||
                          val?.[0] === ECONTRACT_STATUS.IN_PROGRESS
                            ? val?.[1]?.totalAmount
                            : 0),
                        0
                      )
                    )} ${t("lbl_saudi_currency")}`}
                  </h4>
                </div>
                <div className={classNames(styles.info_item, styles.bordered)}>
                  <p className={styles.info_label}>الرصيد المتاح</p>
                  <h4
                    className={classNames(styles.info_value, {
                      [styles.negative]:
                        (stats?.availableBalanceAmount || 0) < 0,
                      [styles.positive]:
                        (stats?.availableBalanceAmount || 0) >= 0,
                    })}>
                    {`${formatAmount(stats?.availableBalanceAmount || 0)} ${t(
                      "lbl_saudi_currency"
                    )}`}
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.settings_container}>
        <BusinessSettings isServiceProvider={isServiceProvider} />
      </div>
    </div>
  );
};

export default OrganizationDetails;
